import { useTheme } from "@mui/material";
import { IconProps } from "./icon.interface";

/**
 * The shared shape of a table action icon: a 24x24 rounded square filled with a
 * semantic colour, holding a white glyph.
 *
 * That is the house style, set by the three icons that shipped first — Edit,
 * Duplicate, Delete — which each hand-roll the same `<rect rx="2">`. This exists
 * so the rest don't hand-roll it a fourth through eighth time; the originals are
 * left alone rather than refactored, since they are working and this is not
 * their change.
 *
 * The single `<svg>` root matters: the Compact view sizes icons with
 * `tbody td svg { width: 20px; height: 20px }`, which only reaches one element.
 * A wrapper `<div>` around a glyph would leave the square at 24px while the
 * glyph inside it shrank — and a nested `<svg>`, which would do the box-fitting
 * below for free, is out for the same reason: that selector matches descendants.
 */

/**
 * Target size for the longer side of a glyph's INK, in the 24-unit square.
 *
 * Measured off Edit (11.74) and Delete (11.67), which set the house style. They
 * are hand-drawn to sit inside a 14x14 clip box but do not fill it — the ink is
 * ~11.7 — so 14 is the wrong number to match against and matching it would make
 * every shared-base icon oversized next to the originals.
 */
const GLYPH_INK = 11.7;

/**
 * The inner box the hand-rolled icons clip themselves to. The ceiling for
 * `inkSize`: past it a glyph starts crowding the square's rounded corners.
 */
const GLYPH_INK_MAX = 14;

const SQUARE = 24;

/**
 * The glyph's own ink box within its 24-unit grid: `[x, y, width, height]`.
 *
 * Required, and that is deliberate. This replaced a fixed
 * `translate(5 5) scale(0.5833)`, which mapped the 24-unit VIEWBOX onto 14 and
 * so assumed every glyph filled its own viewBox. Material's do not, and they
 * disagree wildly: `PlayCircleFilledWhiteOutlined` spans 20 of 24 units,
 * `VisibilityOutlined` 22, `RefreshOutlined` only 16. Under one flat scale that
 * rendered as ink of 11.67, 12.83 and 9.33 against the originals' 11.7 — Refresh
 * 20% small, and `PlayArrowRounded` (the Run glyph that first shipped, spanning
 * 9.7 units) 38% small, which is what made the difference visible on the
 * audience tables.
 *
 * Optional with a default, this would be forgotten and silently reintroduce
 * exactly that drift, so it is a compile error instead.
 *
 * To get it for a new glyph: render the raw `<path>` in a `0 0 24 24` svg and
 * read `path.getBBox()` — x, y, width, height, rounded to 2dp.
 */
export type GlyphBox = [x: number, y: number, width: number, height: number];

export default function ActionIconBase({
  color,
  glyph,
  glyphBox,
  inkSize = GLYPH_INK,
  primaryColor,
  secondaryColor = "#fff",
  disabled = false,
}: IconProps & {
  /** Resolved by the caller off the theme, so each action keeps one meaning. */
  color: string;
  /** Path data in a 0 0 24 24 box. */
  glyph: string;
  /** That path's ink box. See `GlyphBox`. */
  glyphBox: GlyphBox;
  /**
   * Override the target ink size for a glyph that reads light at the default.
   *
   * Matching bounding boxes matches how much ROOM each glyph takes, not how much
   * of it is painted, and for a thin closed ring those come apart. Run
   * (`PlayCircleFilledWhiteOutlined`) is a 2-unit ring on a 20-unit box: at ink
   * 11.7 that lands at a limb of 1.17 against the 1.5 Edit and Delete are
   * stroked at, so it reads 22% lighter than its neighbours while measuring the
   * same. Refresh needs no override precisely because its ring sits on a
   * 16-unit box, so the same normalisation already gives it a limb of 1.46.
   *
   * Reach for it only when a glyph's limb comes out well under the house 1.5 —
   * limb ≈ 2 * inkSize / max(glyphBox width, height) for the Material rings.
   * Keep the result at or under `GLYPH_INK_MAX`.
   */
  inkSize?: number;
}) {
  const theme = useTheme();

  // Fit the ink box into `inkSize`, longest side first so the glyph keeps its
  // proportions — a link or an eye is meant to be wide and short — then centre
  // what is left in the square. Centring on the ink rather than the viewBox also
  // fixes glyphs drawn off-centre in their own grid.
  const [boxX, boxY, boxWidth, boxHeight] = glyphBox;
  const scale =
    Math.min(inkSize, GLYPH_INK_MAX) / Math.max(boxWidth, boxHeight);
  const translateX = (SQUARE - boxWidth * scale) / 2 - boxX * scale;
  const translateY = (SQUARE - boxHeight * scale) / 2 - boxY * scale;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        width="24"
        height="24"
        rx="2"
        fill={disabled ? theme.palette.inactive.main : primaryColor ?? color}
      />
      <g
        transform={`translate(${translateX.toFixed(3)} ${translateY.toFixed(
          3
        )}) scale(${scale.toFixed(4)})`}
      >
        <path d={glyph} fill={secondaryColor} />
      </g>
    </svg>
  );
}
